import { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { db } from "../firebase";
import { collection, onSnapshot } from "firebase/firestore";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import logo from "../assets/logo5.png";
import {
  faMagnifyingGlass,
  faUser,
  faCartShopping,
  faChevronLeft,
  faChevronRight,
  faXmark,
  faBars,
} from "@fortawesome/free-solid-svg-icons";
import "../styles/Navbar.css";

// ─── Marquee messages ───────────────────────────────────────────────
const MARQUEE_MESSAGES = [
  "Wrap your love in a gift.",
  "Celebrate every moment with a perfect gift.",
  "New arrivals every week — explore the catalog",
];

export default function Navbar() {
  const navigate = useNavigate();

  const location = useLocation();


  // ── Marquee state ────────────────────────────────────────────────
  const [msgIndex, setMsgIndex] = useState(0);
  const [animating, setAnimating] = useState(false);

  const changeMsg = (dir) => {
    if (animating) return;
    setAnimating(true);
    setTimeout(() => {
      setMsgIndex((prev) =>
        dir === 1
          ? (prev + 1) % MARQUEE_MESSAGES.length
          : (prev - 1 + MARQUEE_MESSAGES.length) % MARQUEE_MESSAGES.length
      );
      setAnimating(false);
    }, 300);
  };

  useEffect(() => {
    const t = setInterval(() => changeMsg(1), 4000);
    return () => clearInterval(t);
  }, [animating]);

  // ── Mobile menu state ────────────────────────────────────────────
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const menuRef = useRef(null);

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  const handleMenuLinkClick = (path) => {
    navigate(path);
    closeMobileMenu();
  };

  // Close menu on outside click
  useEffect(() => {
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        closeMobileMenu();
      }
    };
    if (mobileMenuOpen) {
      document.addEventListener("mousedown", handler);
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.removeEventListener("mousedown", handler);
      document.body.style.overflow = "auto";
    };
  }, [mobileMenuOpen]);

  // ── Cart badge ───────────────────────────────────────────────────
  const [cartCount, setCartCount] = useState(0);

  useEffect(() => {
    const update = () => {
      try {
        const cart = JSON.parse(localStorage.getItem("cart") || "[]");
        setCartCount(cart.reduce((s, i) => s + (i.quantity || 1), 0));
      } catch {
        setCartCount(0);
      }
    };
    update();
    window.addEventListener("storage", update);
    window.addEventListener("cart-updated", update);
    return () => {
      window.removeEventListener("storage", update);
      window.removeEventListener("cart-updated", update);
    };
  }, []);

  // ── Search ───────────────────────────────────────────────────────
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [allProducts, setAllProducts] = useState([]);
  const [suggestions, setSuggestions] = useState({
    categories: [],
    products: [],
  });
  const [matchedProducts, setMatchedProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const searchRef = useRef(null);

  // Fetch products once for search
  useEffect(() => {
    setLoading(true);
    const unsub = onSnapshot(
      collection(db, "products"),
      (snap) => {
        setAllProducts(
          snap.docs.map((d) => ({ id: d.id, ...d.data() }))
        );
        setLoading(false);
      },
      (error) => {
        console.error("Error fetching products:", error);
        setLoading(false);
      }
    );
    return () => unsub();
  }, []);

  // Open search & focus
  const openSearch = () => {
    setSearchOpen(true);
    setMobileMenuOpen(false);
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const closeSearch = () => {
    setSearchOpen(false);
    setQuery("");
    setSuggestions({
      categories: [],
      products: [],
    });
    setMatchedProducts([]);
  };

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        closeSearch();
      }
    };
    if (searchOpen) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [searchOpen]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        closeSearch();
        closeMobileMenu();
      }
      if ((e.ctrlKey || e.metaKey) && e.key === "k") {
        e.preventDefault();
        openSearch();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Debounced search logic
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      const q = query.trim().toLowerCase();
      if (!q) {
        setSuggestions({
          categories: [],
          products: [],
        });
        setMatchedProducts([]);
        return;
      }

      // Build suggestions: unique partial name matches
      // 🔥 CATEGORY SUGGESTIONS
      const categorySuggestions = [];

      allProducts.forEach((p) => {
        const name = p.categoryName || p.category || "";
        const id = p.categoryId;

        if (
          name.toLowerCase().includes(q) &&
          !categorySuggestions.find((c) => c.name === name)
        ) {
          categorySuggestions.push({
            name,
            id,
          });
        }
      });

      const finalCategories = categorySuggestions.slice(0, 4);

      // 🔥 PRODUCT NAME SUGGESTIONS
      const productSuggestions = [
        ...new Set(
          allProducts
            .filter((p) => p.name?.toLowerCase().includes(q))
            .map((p) => p.name)
        ),
      ].slice(0, 4);

      setSuggestions({
        categories: finalCategories,
        products: productSuggestions,
      });

      // Product results
      const products = allProducts
        .filter((p) => {
          const name = p.name?.toLowerCase() || "";
          const category = p.category?.toLowerCase() || "";
          return name.includes(q) || category.includes(q);
        })
        .slice(0, 5);
      setMatchedProducts(products);
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [query, allProducts]);

  const handleSuggestionClick = (s) => {
    // Find the first product matching this suggestion
    const product = allProducts.find(
      (p) => p.name?.toLowerCase() === s.toLowerCase()
    );

    if (product && product.id) {
      navigate(`/product/${product.id}`);
    } else {
      // Fallback to search results page
      navigate(`/catalog?search=${encodeURIComponent(s)}`);
    }
    closeSearch();
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (query.trim()) {
      navigate(`/catalog?search=${encodeURIComponent(query.trim())}`);
      closeSearch();
    }
  };

  const highlightMatch = (text, q) => {
    const idx = text.toLowerCase().indexOf(q.toLowerCase());
    if (idx === -1) return text;
    return (
      <>
        <span className="nb-search__highlight">{text.slice(0, idx)}</span>
        <strong>{text.slice(idx, idx + q.length)}</strong>
        {text.slice(idx + q.length)}
      </>
    );
  };

  const hasResults =
    (suggestions?.categories?.length || 0) > 0 ||
    (suggestions?.products?.length || 0) > 0 ||
    matchedProducts.length > 0;

  return (
    <>
      {/* ── TOP BAR ─────────────────────────────────────────── */}
      <div className="nb-topbar">
        {/* Rotating message */}
        <div className="nb-topbar__marquee">
          <button
            className="nb-topbar__arrow"
            onClick={() => changeMsg(-1)}
            aria-label="Previous message"
            title="Previous message"
          >
            <FontAwesomeIcon icon={faChevronLeft} />
          </button>

          <div className="nb-topbar__msg-wrap">
            <span
              key={msgIndex}
              className={`nb-topbar__msg ${animating ? "nb-topbar__msg--exit" : "nb-topbar__msg--enter"
                }`}
            >
              {MARQUEE_MESSAGES[msgIndex]}
            </span>
          </div>

          <button
            className="nb-topbar__arrow"
            onClick={() => changeMsg(1)}
            aria-label="Next message"
            title="Next message"
          >
            <FontAwesomeIcon icon={faChevronRight} />
          </button>
        </div>
      </div>

      {/* ── MAIN NAV ─────────────────────────────────────────── */}
      <nav className="nb-nav">
        {/* Hamburger menu button (mobile only) */}
        <button
          className={`nb-nav__hamburger ${mobileMenuOpen ? "nb-nav__hamburger--active" : ""}`}
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Toggle menu"
          title="Toggle navigation menu"
        >
          <FontAwesomeIcon icon={mobileMenuOpen ? faXmark : faBars} />
        </button>

        {/* Left links (desktop only) */}
        <ul className="nb-nav__links">
          <li>
            <Link
              to="/"
              className={`nb-nav__link ${location.pathname === "/" ? "nb-nav__link--active" : ""
                }`}
            >
              Home
            </Link>
          </li>
          <li>
            <Link
              to="/catalog"
              className={`nb-nav__link ${location.pathname === "/catalog" ? "nb-nav__link--active" : ""
                }`}
            >
              Catalog
            </Link>
          </li>
          <li>
            <Link
              to="/contact"
              className={`nb-nav__link ${location.pathname === "/contact" ? "nb-nav__link--active" : ""
                }`}
            >
              Contact
            </Link>
          </li>
        </ul>

        {/* Center logo */}
        <Link to="/" className="nb-nav__logo-wrap">
          <div className="nb-nav__logo-placeholder">
            <img src={logo} alt="Gifts House logo" />
          </div>
        </Link>

        {/* Right icons */}
        <div className="nb-nav__actions">
          <button
            className="nb-nav__icon-btn"
            onClick={openSearch}
            aria-label="Search products"
            title="Search (Ctrl+K)"
          >
            <FontAwesomeIcon icon={faMagnifyingGlass} />
          </button>
          <Link
            to="/cart"
            className="nb-nav__icon-btn nb-nav__cart-btn"
            aria-label={`Cart (${cartCount} items)`}
            title={`View cart (${cartCount} items)`}
          >
            <FontAwesomeIcon icon={faCartShopping} />
            {cartCount > 0 && (
              <span className="nb-nav__cart-badge">{cartCount}</span>
            )}
          </Link>
        </div>
      </nav>
      {/* ── MOBILE MENU OVERLAY ──────────────────────────────── */}
      {mobileMenuOpen && <div className="nb-mobile-overlay" onClick={closeMobileMenu} />}

      {/* ── MOBILE MENU ───────────────────────────────────────── */}
      <div
        className={`nb-mobile-menu ${mobileMenuOpen ? "nb-mobile-menu--open" : ""}`}
        ref={menuRef}
      >
        <div className="nb-mobile-menu__header">
          <h2 className="nb-mobile-menu__title">Menu</h2>
          <button
            className="nb-mobile-menu__close"
            onClick={closeMobileMenu}
            aria-label="Close menu"
            title="Close menu"
          >
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>

        <nav className="nb-mobile-menu__nav">
          <Link
            to="/"
            className="nb-mobile-menu__link nb-mobile-menu__link--active"
            onClick={() => handleMenuLinkClick("/")}
          >
            Home
          </Link>
          <Link
            to="/catalog"
            className="nb-mobile-menu__link"
            onClick={() => handleMenuLinkClick("/catalog")}
          >
            Catalog
          </Link>
          <Link
            to="/contact"
            className="nb-mobile-menu__link"
            onClick={() => handleMenuLinkClick("/contact")}
          >
            Contact
          </Link>
        </nav>

        <div className="nb-mobile-menu__divider" />

        <div className="nb-mobile-menu__actions">
          <Link
            to="/cart"
            className="nb-mobile-menu__action-btn"
            onClick={() => handleMenuLinkClick("/cart")}
          >
            <FontAwesomeIcon icon={faCartShopping} />
            <span>Cart {cartCount > 0 && `(${cartCount})`}</span>
          </Link>
        </div>
      </div>

      {/* ── SEARCH OVERLAY ───────────────────────────────────── */}
      {searchOpen && (
        <div className="nb-search__overlay">
          <div className="nb-search__panel" ref={searchRef}>
            {/* Search input row */}
            <form className="nb-search__row" onSubmit={handleSearchSubmit} noValidate>
              <label className="nb-search__label" htmlFor="nb-search-input">
                Search
              </label>
              <input
                id="nb-search-input"
                ref={inputRef}
                className="nb-search__input"
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                autoComplete="off"
              />
              <button
                type="button"
                className="nb-search__clear"
                onClick={closeSearch}
                aria-label="Close search"
                title="Close (Esc)"
              >
                <FontAwesomeIcon icon={faXmark} />
              </button>
              <button
                type="submit"
                className="nb-search__submit"
                aria-label="Submit search"
                title="Submit search"
              >
                <FontAwesomeIcon icon={faMagnifyingGlass} />
              </button>
            </form>

            {/* Divider */}
            <div className="nb-search__divider" />

            {/* Loading state */}
            {loading && (
              <div className="nb-search__loading">
                <p>Loading products...</p>
              </div>
            )}

            {/* Results */}
            {hasResults && (
              <div className="nb-search__results">
                {/* Suggestions column */}
                {(suggestions.categories.length > 0 ||
                  suggestions.products.length > 0) && (
                    <div className="nb-search__col nb-search__col--suggestions">

                      {/* CATEGORY */}
                      {suggestions.categories.length > 0 && (
                        <>
                          <p className="nb-search__col-title">Categories</p>
                          <ul className="nb-search__suggestion-list">
                            {suggestions.categories.map((c, i) => (
                              <li key={i}>
                                <button
                                  className="nb-search__suggestion-btn"
                                  onClick={() => {
                                    navigate(`/category/${c.id}`);
                                    closeSearch(); // 🔥 VERY IMPORTANT
                                  }}
                                >
                                  {c.name}
                                </button>
                              </li>
                            ))}
                          </ul>
                        </>
                      )}
                    </div>
                  )}

                {/* Products column */}
                {matchedProducts.length > 0 && (
                  <div className="nb-search__col nb-search__col--products">
                    <p className="nb-search__col-title">Products</p>
                    <div className="nb-search__divider nb-search__divider--col" />
                    <ul className="nb-search__product-list">
                      {matchedProducts.map((p) => (
                        <li key={p.id}>
                          <Link
                            to={`/product/${p.id}`}
                            className="nb-search__product-item"
                            onClick={closeSearch}
                          >
                            <div className="nb-search__product-thumb">
                              {p.image || (p.images && p.images[0]) ? (
                                <img
                                  src={p.image || p.images[0]}
                                  alt={p.name}
                                  loading="lazy"
                                />
                              ) : (
                                <div className="nb-search__product-thumb-placeholder">
                                  🎁
                                </div>
                              )}
                            </div>
                            <div className="nb-search__product-info">
                              <span className="nb-search__product-name">
                                {highlightMatch(p.name, query)}
                              </span>
                              <span className="nb-search__product-price">
                                Rs. {p.price?.toFixed(2)}
                              </span>
                            </div>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {/* No results state */}
            {query.trim() && !hasResults && !loading && (
              <div className="nb-search__no-results">
                <p>No products found for "{query}"</p>
              </div>
            )}

            {/* "Search for …" or "Browse all" footer */}
            {query.trim() && (
              <>
                <div className="nb-search__divider" />
                <button
                  className="nb-search__footer-link"
                  onClick={handleSearchSubmit}
                  type="button"
                >
                  Search for &ldquo;{query}&rdquo;
                  <FontAwesomeIcon
                    icon={faChevronRight}
                    className="nb-search__footer-arrow"
                  />
                </button>
              </>
            )}

            {!query.trim() && (
              <>
                <div className="nb-search__divider" />
                <Link to="/catalog" className="nb-search__footer-link" onClick={closeSearch}>
                  Browse all products
                  <FontAwesomeIcon
                    icon={faChevronRight}
                    className="nb-search__footer-arrow"
                  />
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
